/**
 * Serialises session refreshes across the tabs of one browser.
 *
 * The refresh token is single use, so two tabs that notice the same dead context token post
 * the same token: one wins and rotates it, the other is refused — and then dropped the
 * winner's fresh token out of localStorage on its way out, ending a session that was very
 * much alive and logging both tabs out (PORTAL-6084 review, RISK-02).
 *
 * Web Locks is the only cross-tab mutex the platform offers that releases itself when the
 * holder's tab dies; a flag in localStorage would strand every other tab behind a crashed
 * one. Where it is missing (Safari below 15.4, any non-browser environment, the unit tests)
 * the callback simply runs unserialised — the compare-and-swap in the auth store is what
 * keeps that case correct, the lock only keeps the race from being reached at all.
 */

// TypeScript 4.5's lib.dom.d.ts predates the Web Locks API, so the slice we use is declared
// here instead of through a `lib` bump that would change type checking for every file.
type LockGrantedCallback = () => Promise<unknown>
interface LockManagerLike {
  request(name: string, callback: LockGrantedCallback): Promise<unknown>
}

const SESSION_LOCK_NAME = "eecom-teamportal-session-refresh"

const lockManager = (): LockManagerLike | null => {
  if (typeof navigator === "undefined") {
    return null
  }
  const locks = (navigator as unknown as { locks?: LockManagerLike }).locks
  return locks && typeof locks.request === "function" ? locks : null
}

/**
 * Runs `task` while holding the session lock, or straight away where the API is missing.
 * Whatever `task` resolves with is passed through; a rejection propagates and releases the
 * lock, exactly as it would without one.
 */
export const withSessionLock = async <T>(task: () => Promise<T>): Promise<T> => {
  const locks = lockManager()
  if (!locks) {
    return task()
  }
  return (await locks.request(SESSION_LOCK_NAME, task as LockGrantedCallback)) as T
}
